/**
 * 점검 화면 컴포넌트
 * 점검 중일 때 전체 화면에 표시
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActiveMaintenanceResponse } from '../types/maintenance';
import { format } from 'date-fns';
import '../styles/MaintenanceScreen.css';

interface MaintenanceScreenProps {
    maintenance: ActiveMaintenanceResponse;
    showDashboardButton?: boolean; // 대시보드 이동 버튼 표시 여부
    onDashboardClick?: () => void; // 대시보드 버튼 클릭 핸들러
}

/**
 * 날짜 포맷팅 헬퍼
 * LocalDateTime 배열 형식 및 문자열 형식 모두 지원
 */
const formatDateTime = (dateValue: string | number[] | null): string => {
    if (!dateValue) return '-';
    try {
        // 배열 형식: [2026, 1, 6, 14, 0, 0] (LocalDateTime 직렬화)
        if (Array.isArray(dateValue)) {
            const [year, month, day, hour = 0, minute = 0] = dateValue;
            const date = new Date(year, month - 1, day, hour, minute);
            return format(date, 'yyyy년 MM월 dd일 HH:mm');
        }
        return format(new Date(dateValue), 'yyyy년 MM월 dd일 HH:mm');
    } catch {
        return String(dateValue);
    }
};

const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ maintenance, showDashboardButton = false, onDashboardClick }) => {
    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleGoToDashboard = () => {
        // 부모 컴포넌트의 핸들러가 있으면 실행 (확인 상태 저장)
        if (onDashboardClick) {
            onDashboardClick();
        }
        // 대시보드로 이동
        navigate('/delivery');
    };

    return (
        <div className="maintenance-screen">
            <div className="maintenance-content">
                {/* 뒤로가기 버튼 - 대시보드 버튼이 없을 때만 표시 */}
                {!showDashboardButton && (
                    <button 
                        className="maintenance-back-button" 
                        onClick={handleGoBack}
                        aria-label="뒤로가기"
                    >
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="currentColor"
                            width="24"
                            height="24"
                        >
                            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                        </svg>
                    </button>
                )}

                <div className="maintenance-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        width="64"
                        height="64"
                    >
                        <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                    </svg>
                </div>
                <h1 className="maintenance-title">시스템 점검 중</h1>
                <p className="maintenance-message">
                    {maintenance.msgContent || '서비스 점검 중입니다. 잠시 후 다시 이용해주세요.'}
                </p>
                <div className="maintenance-schedule">
                    <div className="schedule-item">
                        <span className="schedule-label">점검 시작</span>
                        <span className="schedule-value">{formatDateTime(maintenance.startDate)}</span>
                    </div>
                    <div className="schedule-item">
                        <span className="schedule-label">점검 종료 예정</span>
                        <span className="schedule-value">{formatDateTime(maintenance.endDate)}</span>
                    </div>
                </div>
                <p className="maintenance-notice">
                    점검 시간은 상황에 따라 변경될 수 있습니다.<br />
                    일부 기능이 제한됩니다.
                </p>

                {/* 대시보드 이동 버튼 */}
                {showDashboardButton && (
                    <button 
                        className="maintenance-dashboard-button" 
                        onClick={handleGoToDashboard}
                    >
                        대시보드로 이동
                    </button>
                )}
            </div>
        </div>
    );
};

export default MaintenanceScreen;
